// Contrato de la pantalla Agenda del Portal Cobrador. Describe la FILA que la pantalla pinta, no un
// DTO del silo: hoy no existe endpoint de agenda y los datos salen de `fixtures.ts`.

/** Canal por el que se contactó al cliente. Los rótulos visibles viven en `format.ts`. */
export type ContactChannel = 'call' | 'sms' | 'chat' | 'whatsapp' | 'voice_blaster';

/** Cómo terminó el intento de contacto. */
export type ContactOutcome =
    | 'spoke'
    | 'promise'
    | 'no_answer'
    | 'wrong_number'
    | 'voicemail'
    | 'sent';

/** Estado de un compromiso de pago. */
export type PromiseState = 'active' | 'fulfilled' | 'broken';

/** Por dónde se comprometió o se recibió el pago. */
export type PaymentChannel = 'store' | 'oxxo' | 'app' | 'pcf_auto' | 'pcf_manual';

/** Estado del crédito en el hero de la ficha. */
export type AgendaCreditStatus =
    | 'pending_activation'
    | 'active'
    | 'in_arrears'
    | 'settled'
    | 'cancelled'
    | 'discarded'
    | 'written_off';

export interface AgendaContact {
    /** Marca de tiempo local `YYYY-MM-DDTHH:mm`. */
    at: string;
    channel: ContactChannel;
    outcome: ContactOutcome;
    note: string;
    /** Id del cobrador, o `system` cuando lo disparó la plataforma. */
    collectorId: string;
}

export interface AgendaPromise {
    /** Día comprometido `YYYY-MM-DD`. */
    day: string;
    amountCents: number;
    channel: PaymentChannel;
    state: PromiseState;
    /** Día en que el cobrador lo registró. */
    registeredOn: string;
    collectorId: string;
}

export interface AgendaPayment {
    /** Número de cupón del plan de pagos. */
    installmentNumber: number;
    amountCents: number;
    paidOn: string;
    channel: PaymentChannel;
}

export interface AgendaDevice {
    imei: string;
    /** `locked` bloqueado por mora, `active` en uso normal. */
    state: 'locked' | 'active';
    /** Día desde el que está bloqueado; ausente cuando no lo está. */
    lockedSince?: string;
}

/** Los 5 rangos de prioridad del mock, en el orden en que la agenda se recorre. */
export type PriorityRank = 1 | 2 | 3 | 4 | 5;

export interface AgendaPriority {
    rank: PriorityRank;
    label: string;
}

/** Una fila de la agenda: el crédito más todo lo que el cobrador necesita para decidir a quién llamar. */
export interface CollectorAgendaRow {
    creditId: string;
    customerId: string;
    customerName: string;
    storeId: string;
    storeName: string;
    productLabel: string;
    status: AgendaCreditStatus;
    /** Día de originación, en el chip del hero. */
    originatedOn: string;
    /** Cuota semanal fija con IVA: lo que se cobra cada semana. */
    weeklyPaymentWithIvaCents: number;
    overdueDays: number;
    /** Atrasos vitalicios del cliente: a partir de 3 la regla de desbloqueo endurece. */
    accumulatedArrears: number;
    overdueInstallments: number;
    overdueAmountCents: number;
    device: AgendaDevice | null;
    contacts: readonly AgendaContact[];
    promises: readonly AgendaPromise[];
    payments: readonly AgendaPayment[];
    /** Intentos sin respuesta en las últimas 48 h, para el badge `n/3` de la tabla. */
    recentAttempts: number;
}

/** Los tramos de «Días de mora» del panel de filtros. */
export type ArrearsBucket = '1-7' | '8-30' | '31-60' | '61-90' | '90+';

/** Lo que ofrece el combo «Canal último contacto»: un canal, o una ausencia de contacto. */
export type ContactChannelFilter = ContactChannel | 'no_contact_24h' | 'no_contact_3d';

/** Los tres interruptores del grupo «Extra». */
export type AgendaFlag = 'with_promise' | 'broken_promise' | 'locked_device';

export interface AgendaFilters {
    /** CR, cliente o IMEI. */
    search: string;
    /** Id de tienda; cadena vacía es «Todas». */
    store: string;
    /** Cadena vacía es «Todos». */
    arrears: ArrearsBucket | '';
    /** Cadena vacía es «Todos». */
    channel: ContactChannelFilter | '';
    /** Rangos en CENTAVOS; `null` es «sin tope». */
    minAmountCents: number | null;
    maxAmountCents: number | null;
    flags: readonly AgendaFlag[];
}

export const emptyAgendaFilters: AgendaFilters = {
    search: '',
    store: '',
    arrears: '',
    channel: '',
    minAmountCents: null,
    maxAmountCents: null,
    flags: [],
};

/** Las 5 pastillas de arriba de la tabla: además de contar, filtran. */
export type AgendaStatKey = 'all' | 'broken' | 'today' | 'over30' | 'locked';

export const AGENDA_STAT_KEYS: readonly AgendaStatKey[] = [
    'all',
    'broken',
    'today',
    'over30',
    'locked',
];

export type AgendaStats = Record<AgendaStatKey, number>;

/** Las tres métricas informativas de la barra de herramientas. */
export interface AgendaMetrics {
    recoveredTodayCents: number;
    /** Compromisos cumplidos sobre los ya vencidos, en porcentaje entero. */
    cureRatePercent: number;
    noContactThreeDays: number;
}

/** Por qué columna se ordena la tabla. */
export type AgendaSortKey =
    | 'priority'
    | 'creditId'
    | 'customer'
    | 'overdueInstallments'
    | 'overdueAmount'
    | 'overdueDays'
    | 'lastContact'
    | 'promise';

export interface AgendaSort {
    key: AgendaSortKey;
    direction: 'asc' | 'desc';
}

/** Cuál de los cajones de acción está abierto. `null` = ninguno. */
export type AgendaDrawerKey = 'contact' | 'promise' | 'payment' | 'link' | 'restructure' | 'more';
